import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Globe, Mail, Phone, Share2, Bookmark, QrCode, Download, BadgeCheck, ExternalLink, Loader2, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MEDIA } from "@/lib/media";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { brand: "Brand", outfitter: "Outfitter", guide: "Guide", state: "State Partner" };
const OFFERING_HEADING = { brand: "Featured Products", outfitter: "Hunt Packages", guide: "Hunt Packages", state: "Resources" };
const UPDATE_KIND_LABEL = { news: "News", new_arrival: "New Arrival", hunt_report: "Hunt Report", event: "Event", sale: "Sale" };
const VERIFY_LABEL = {
  verified_business: "Verified Brand", verified_retailer: "Verified Brand", verified_outfitter: "Verified Outfitter",
  verified_guide: "Verified Guide", official_state: "Official State Partner",
};

function sessionId() {
  let s = localStorage.getItem("hr_sid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("hr_sid", s); }
  return s;
}

export default function PartnerProfile({ prefix = "brands" }) {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const src = sp.get("src") || "direct";
  const ref = sp.get("ref") || undefined;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    setData(null); setErr(false);
    api.publicPartner(prefix, slug, { session_id: sessionId(), src, ref })
      .then((d) => {
        setData(d);
        if (d?.partner?.id) api.partnerPublicUpdates(d.partner.id, { limit: 6 }).then((r) => setUpdates(r.updates || [])).catch(() => {});
      }).catch(() => setErr(true));
  }, [prefix, slug, src, ref]);

  if (err) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-4xl text-forest">Partner not found</h1>
        <Link to="/" className="text-blaze font-semibold mt-4 inline-block">Back to HomeRange</Link>
      </div><Footer />
    </div>
  );
  if (!data) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  const p = data.partner;
  const bid = p.id;
  const verify = VERIFY_LABEL[p.verification_status];
  const qrPng = `${BACKEND}/api/partners/${bid}/qr.png?box=16`;
  const profileUrl = typeof window !== "undefined" ? window.location.origin + data.public_path : data.public_path;
  const track = (event, extra = {}) => api.partnerTrack(bid, { event, session_id: sessionId(), ...extra }).catch(() => {});
  const socials = p.socials || {};
  const offerings = p.offerings || [];
  const gallery = p.gallery || [];
  const offeringHeading = OFFERING_HEADING[p.partner_type] || "Offerings";

  const share = async () => {
    track("save", {});
    try {
      if (navigator.share) { await navigator.share({ title: p.name, url: profileUrl }); }
      else { await navigator.clipboard.writeText(profileUrl); toast.success("Profile link copied"); }
    } catch { /* cancelled */ }
  };
  const copyLink = async () => { await navigator.clipboard.writeText(profileUrl); toast.success("Profile link copied"); };

  return (
    <div className="bg-paper min-h-screen" data-testid="partner-profile">
      <SiteHeader />

      {/* Cover + identity */}
      <section className="relative">
        <div className="h-52 sm:h-72 bg-charcoal overflow-hidden">
          {p.cover ? <img src={p.cover} alt={`${p.name} cover`} className="w-full h-full object-cover" style={p.cover_pos ? { objectPosition: p.cover_pos } : undefined} />
            : <img src={MEDIA.exploreRecipes} alt="" className="w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-14 relative">
            <div className="w-28 h-28 rounded-2xl bg-white border border-saddle/20 shadow-lg overflow-hidden grid place-items-center shrink-0">
              {p.logo ? <img src={p.logo} alt={`${p.name} logo`} className="w-full h-full object-cover" /> : <span className="font-serif text-3xl text-forest">{(p.name || "H")[0]}</span>}
            </div>
            <div className="flex-1 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-3xl sm:text-4xl text-forest" data-testid="partner-name">{p.name}</h1>
                {verify && <span className="inline-flex items-center gap-1 text-xs font-semibold text-blaze bg-blaze/10 px-2 py-1 rounded-full" data-testid="partner-verified"><BadgeCheck size={14} /> {verify}</span>}
              </div>
              <p className="text-sm text-charcoal/60 mt-1">{TYPE_LABEL[p.partner_type] || "HomeRange Partner"}{p.location ? ` · ${p.location}` : ""}</p>
              {p.tagline && <p className="text-charcoal/80 mt-1.5 italic">{p.tagline}</p>}
            </div>
            <div className="flex items-center gap-2 pb-1">
              <button onClick={() => setShowQr(true)} className="inline-flex items-center gap-1.5 border border-saddle/25 bg-white px-3 py-2 rounded-full text-sm font-semibold text-charcoal/80 hover:border-forest transition-colors" data-testid="partner-qr-btn"><QrCode size={16} /> QR</button>
              <button onClick={share} className="inline-flex items-center gap-1.5 border border-saddle/25 bg-white px-3 py-2 rounded-full text-sm font-semibold text-charcoal/80 hover:border-forest transition-colors" data-testid="partner-share-btn"><Share2 size={16} /> Share</button>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1fr_320px] gap-10">
        <div>
          {(p.about || p.description) && (
            <section data-testid="partner-about">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest">About</h2>
              <p className="text-charcoal/80 mt-3 leading-relaxed whitespace-pre-wrap">{p.about || p.description}</p>
            </section>
          )}
          {updates.length > 0 && (
            <section className="mt-10" data-testid="partner-updates-section">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest">Latest Updates</h2>
              <div className="space-y-4 mt-4">
                {updates.map((u) => (
                  <article key={u.id} className="border border-saddle/20 bg-white rounded-lg p-4 flex gap-4" data-testid={`partner-update-${u.id}`}>
                    {u.media && <img src={u.media} alt="" loading="lazy" className="w-24 h-24 object-cover rounded shrink-0" />}
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold uppercase bg-blaze/10 text-blaze px-2 py-0.5 rounded-full">{UPDATE_KIND_LABEL[u.post_kind] || "News"}</span>
                      {u.title && <h3 className="font-serif text-lg text-forest mt-1.5">{u.title}</h3>}
                      {u.text && <p className="text-sm text-charcoal/75 mt-1 leading-relaxed whitespace-pre-wrap">{u.text}</p>}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
          {offerings.length > 0 && (
            <section className="mt-10" data-testid="partner-offerings">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest">{offeringHeading}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {offerings.map((o) => {
                  const Card = o.link ? "a" : "div";
                  const cardProps = o.link ? { href: o.link, target: "_blank", rel: "noreferrer", onClick: () => track("website_click", { src: "offering" }) } : {};
                  return (
                    <Card key={o.id} {...cardProps} className={`block border border-saddle/20 bg-white rounded-lg overflow-hidden ${o.link ? "hover:border-forest transition-colors" : ""}`} data-testid={`offering-card-${o.id}`}>
                      {o.image && <img src={o.image} alt={o.title} loading="lazy" className="w-full h-40 object-cover" />}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-forest">{o.title}</p>
                          {o.price && <span className="text-sm font-semibold text-blaze whitespace-nowrap">{o.price}</span>}
                        </div>
                        {(o.species || o.duration) && <p className="text-xs text-charcoal/60 mt-1">{[o.species, o.duration].filter(Boolean).join(" · ")}</p>}
                        {o.description && <p className="text-sm text-charcoal/75 mt-2 leading-relaxed">{o.description}</p>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}
          {gallery.length > 0 && (
            <section className="mt-10" data-testid="partner-gallery">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest">Gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {gallery.map((url, i) => (
                  <button key={url} onClick={() => setLightbox(url)} className="aspect-square rounded-lg overflow-hidden border border-saddle/15 group" data-testid={`gallery-photo-${i}`}>
                    <img src={url} alt={`${p.name} photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </button>
                ))}
              </div>
            </section>
          )}
          {data.listings?.length > 0 && (
            <section className="mt-10" data-testid="partner-listings">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest">Listings</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {data.listings.map((l) => (
                  <Link key={l.id} to={`/marketplace/listings/${l.id}`} className="group border border-saddle/20 bg-white rounded-lg overflow-hidden hover:border-forest transition-colors">
                    {l.images?.[0] && <img src={l.images[0]} alt={l.title} loading="lazy" className="w-full h-36 object-cover" />}
                    <div className="p-3">
                      <p className="font-semibold text-forest">{l.title}</p>
                      <p className="text-xs text-charcoal/60 mt-0.5">{[l.species, l.state].filter(Boolean).join(" · ")}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {p.cta_url && (
            <a href={p.cta_url} target="_blank" rel="noreferrer" onClick={() => track("website_click", { src: "cta" })}
              className="flex items-center justify-center gap-2 bg-blaze text-white px-5 py-3 rounded-full font-semibold hover:bg-blaze-light transition-colors" data-testid="partner-cta">
              {p.cta_label || "Learn more"} <ArrowRight size={16} />
            </a>
          )}
          <div className="border border-saddle/20 bg-white rounded-xl p-5 space-y-3 text-sm">
            {p.states_served?.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-saddle">States served</p>
                <div className="flex flex-wrap gap-1.5 mt-2">{p.states_served.map((s) => <span key={s} className="text-xs bg-paper border border-saddle/20 px-2 py-0.5 rounded-full text-charcoal/75">{s}</span>)}</div>
              </div>
            )}
            {p.website && <a href={p.website} target="_blank" rel="noreferrer" onClick={() => track("website_click")} className="flex items-center gap-2 text-charcoal/80 hover:text-blaze" data-testid="partner-website"><Globe size={15} /> Website <ExternalLink size={12} /></a>}
            {p.email && <a href={`mailto:${p.email}`} onClick={() => track("contact_click", { src: "email" })} className="flex items-center gap-2 text-charcoal/80 hover:text-blaze" data-testid="partner-email"><Mail size={15} /> {p.email}</a>}
            {p.phone && <a href={`tel:${p.phone}`} onClick={() => track("contact_click", { src: "phone" })} className="flex items-center gap-2 text-charcoal/80 hover:text-blaze"><Phone size={15} /> {p.phone}</a>}
            {p.location && <p className="flex items-center gap-2 text-charcoal/70"><MapPin size={15} /> {p.location}</p>}
            {Object.keys(socials).length > 0 && (
              <div className="flex gap-3 pt-1">
                {Object.entries(socials).map(([k, v]) => v ? <a key={k} href={v.startsWith("http") ? v : `https://${k}.com/${v}`} target="_blank" rel="noreferrer" onClick={() => track("website_click", { src: k })} className="text-xs font-semibold text-blaze capitalize">{k}</a> : null)}
              </div>
            )}
          </div>
          <button onClick={copyLink} className="w-full text-sm text-charcoal/60 hover:text-blaze inline-flex items-center justify-center gap-1.5" data-testid="partner-copy-link"><Bookmark size={14} /> Copy profile link</button>
        </aside>
      </main>

      {showQr && (
        <div className="fixed inset-0 z-[70] bg-black/70 grid place-items-center p-4" onClick={() => setShowQr(false)} data-testid="partner-qr-modal">
          <div className="bg-white rounded-2xl p-6 max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><h3 className="font-serif text-xl text-forest">Scan to visit</h3><button onClick={() => setShowQr(false)} aria-label="Close"><X size={20} className="text-charcoal/50" /></button></div>
            <img src={qrPng} alt={`${p.name} QR code`} className="w-full rounded-lg border border-saddle/15" data-testid="partner-qr-image" />
            <p className="text-xs text-charcoal/55 mt-2 break-all">{profileUrl}</p>
            <div className="flex gap-2 mt-4">
              <a href={qrPng} download className="flex-1 inline-flex items-center justify-center gap-1.5 bg-forest text-bone px-3 py-2 rounded-full text-sm font-semibold" data-testid="partner-qr-download-png"><Download size={15} /> PNG</a>
              <a href={`${BACKEND}/api/partners/${bid}/qr.svg`} download className="flex-1 inline-flex items-center justify-center gap-1.5 border border-saddle/25 px-3 py-2 rounded-full text-sm font-semibold text-charcoal/80"><Download size={15} /> SVG</a>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/85 grid place-items-center p-4" onClick={() => setLightbox(null)} data-testid="partner-lightbox">
          <button onClick={() => setLightbox(null)} aria-label="Close" className="absolute top-5 right-5 text-white/80 hover:text-white"><X size={28} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-[85vh] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      <Footer />
    </div>
  );
}
